import { useEffect } from 'react'
import { Outlet, useLocation, useLoaderData } from 'react-router-dom'
import { AnnouncementBar } from './AnnouncementBar'
import { Nav } from './Nav'
import { Footer } from './Footer'
import type { Announcement } from '@/lib/wp'

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

export default function RootLayout() {
  const data = useLoaderData() as { announcement: Announcement | null } | undefined
  // Smooth scroll (Lenis) bridged to GSAP ScrollTrigger — client-only, skipped
  // entirely for reduced-motion users. Lazy-loaded so it never blocks first paint.
  useEffect(() => {
    if (prefersReducedMotion()) return
    let destroy: (() => void) | undefined
    let cancelled = false
    import('@/lib/smoothScroll').then(({ initSmoothScroll }) => {
      if (!cancelled) destroy = initSmoothScroll()
    })
    return () => {
      cancelled = true
      destroy?.()
    }
  }, [])

  // Lightweight scroll-reveal (IntersectionObserver — no library, no bundle cost).
  // Re-scans on every route change and can never leave content invisible.
  const { pathname } = useLocation()
  useEffect(() => {
    const els = Array.from(document.querySelectorAll<HTMLElement>('.reveal:not(.reveal-in)'))
    if (prefersReducedMotion()) {
      els.forEach((el) => el.classList.add('reveal-in'))
      return
    }
    const io = new IntersectionObserver(
      (entries, obs) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add('reveal-in')
            obs.unobserve(e.target)
          }
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.15 },
    )
    els.forEach((el) => io.observe(el))
    // Safety net: guarantee visibility even if the observer never fires.
    const safety = window.setTimeout(() => els.forEach((el) => el.classList.add('reveal-in')), 1600)
    return () => {
      io.disconnect()
      clearTimeout(safety)
    }
  }, [pathname])

  return (
    <>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-energy focus:px-4 focus:py-2 focus:text-void"
      >
        Skip to content
      </a>
      <AnnouncementBar initial={data?.announcement ?? null} />
      <Nav />
      <main id="main">
        <Outlet />
      </main>
      <Footer />
    </>
  )
}
